import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  Wrench, 
  ChefHat, 
  Film,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import RecipeGenerator from '../tools/RecipeGenerator';
import { Tool } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Tools section component - Swiss knife functionality
 * Features modular tools including recipe generator
 */

const ToolsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: ${props => props.theme.spacing[8]};
  align-items: start;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing[6]};
  }
`;

const ToolsSidebar = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  padding: ${props => props.theme.spacing[6]};
  border: 1px solid ${props => props.theme.colors.border};
  height: fit-content;
  position: sticky;
  top: 100px;
`;

const SidebarTitle = styled.h3`
  font-size: ${props => props.theme.typography.sizes.xl};
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing[6]};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
`;

const ToolsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[3]};
`;

const ToolItem = styled(motion.button)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  padding: ${props => props.theme.spacing[4]};
  border: none;
  border-radius: ${props => props.theme.borderRadius.lg};
  background-color: ${props => props.$active 
    ? props.theme.colors.primary[100] 
    : 'transparent'};
  color: ${props => props.$active 
    ? props.theme.colors.primary[700] 
    : props.theme.colors.text};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};
  text-align: left;
  width: 100%;

  &:hover {
    background-color: ${props => props.theme.colors.primary[50]};
    transform: translateX(4px);
  }
`;

const ToolIcon = styled.div<{ $active: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.lg};
  background: ${props => props.$active 
    ? props.theme.colors.primary[600] 
    : props.theme.colors.neutral[200]};
  color: ${props => props.$active ? 'white' : props.theme.colors.neutral[600]};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};
`;

const ToolInfo = styled.div`
  flex: 1;
`;

const ToolName = styled.h4`
  font-size: ${props => props.theme.typography.sizes.base};
  font-weight: ${props => props.theme.typography.weights.semibold};
  margin-bottom: ${props => props.theme.spacing[1]};
`;

const ToolDescription = styled.p`
  font-size: ${props => props.theme.typography.sizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${props => props.theme.typography.lineHeights.tight};
`;

const ToolContent = styled.div`
  min-height: 500px;
`;

const ComingSoonCard = styled(motion.div)`
  background: ${props => props.theme.gradients.cosmic};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  padding: ${props => props.theme.spacing[12]};
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23dots)"/></svg>');
    opacity: 0.3;
    pointer-events: none;
  }
`;

const ComingSoonIcon = styled.div`
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing[6]};
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

const ComingSoonTitle = styled.h3`
  font-size: ${props => props.theme.typography.sizes['2xl']};
  font-weight: ${props => props.theme.typography.weights.bold};
  margin-bottom: ${props => props.theme.spacing[4]};
  color: white;
`;

const ComingSoonDescription = styled.p`
  font-size: ${props => props.theme.typography.sizes.lg};
  opacity: 0.9;
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  max-width: 400px;
  margin: 0 auto;
`;

const FeatureList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing[4]};
  margin-top: ${props => props.theme.spacing[8]};
`;

const FeatureCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing[6]};
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
`;

const FeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing[3]};
  color: white;
`;

const FeatureTitle = styled.h4`
  font-size: ${props => props.theme.typography.sizes.base};
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: white;
  margin-bottom: ${props => props.theme.spacing[2]};
`;

const FeatureDescription = styled.p`
  font-size: ${props => props.theme.typography.sizes.sm};
  opacity: 0.8;
  line-height: ${props => props.theme.typography.lineHeights.tight};
`;

const Tools: React.FC = () => {
  const { t } = useLanguage();
  
  // Tools data with dynamic translations
  const tools: Tool[] = useMemo(() => [
    {
      id: 'recipe-generator',
      title: t('tools.recipe.title'),
      description: t('tools.recipe.description'),
      icon: 'chef-hat',
      category: 'utility',
      component: RecipeGenerator,
      featured: true,
      tags: ['food', 'cooking', 'ai', 'utility']
    },
    {
      id: 'red-thread',
      title: t('tools.redthread.title'),
      description: t('tools.redthread.description'),
      icon: 'film',
      category: 'entertainment',
      component: null,
      featured: false,
      tags: ['movies', 'recommendations', 'ai', 'entertainment']
    }
  ], [t]);
  
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  
  // Initialize selectedTool after tools is defined
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!selectedTool && tools.length > 0) {
      setSelectedTool(tools[0]);
    }
  }, [selectedTool, tools]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 },
    },
  };

  const getToolIcon = (iconName: string) => {
    const icons = {
      'chef-hat': <ChefHat size={20} />,
      'film': <Film size={20} />,
    };
    return icons[iconName as keyof typeof icons] || <Wrench size={20} />;
  };

  const renderToolContent = () => {
    if (!selectedTool) {
      return <div>Loading...</div>;
    }
    
    if (selectedTool.component) {
      return <selectedTool.component />;
    }

    return (
      <ComingSoonCard
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
      >
        <ComingSoonIcon>
          {getToolIcon(selectedTool.icon)}
        </ComingSoonIcon>
        <ComingSoonTitle>{selectedTool.title}</ComingSoonTitle>
        <ComingSoonDescription>
          {selectedTool.description}
        </ComingSoonDescription>
        
        <FeatureList>
          <FeatureCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <FeatureIcon>
              <Sparkles size={20} />
            </FeatureIcon>
            <FeatureTitle>{t('tools.comingsoon.feature.development')}</FeatureTitle>
            <FeatureDescription>
              {t('tools.comingsoon.feature.development.desc')}
            </FeatureDescription>
          </FeatureCard>
          
          <FeatureCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <FeatureIcon>
              <ArrowRight size={20} />
            </FeatureIcon>
            <FeatureTitle>{t('tools.comingsoon.feature.available')}</FeatureTitle>
            <FeatureDescription>
              {t('tools.comingsoon.feature.available.desc')}
            </FeatureDescription>
          </FeatureCard>
        </FeatureList>
      </ComingSoonCard>
    );
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-100px' }}
    >
      <ToolsContainer>
        <ToolsSidebar>
          <SidebarTitle>
            <Wrench size={24} />
            {t('tools.title')}
          </SidebarTitle>
          <ToolsList>
            {tools.map((tool, index) => (
              <ToolItem
                key={tool.id}
                $active={selectedTool?.id === tool.id}
                onClick={() => setSelectedTool(tool)}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ToolIcon $active={selectedTool?.id === tool.id}>
                  {getToolIcon(tool.icon)}
                </ToolIcon>
                <ToolInfo>
                  <ToolName>{tool.title}</ToolName>
                  <ToolDescription>{tool.description}</ToolDescription>
                </ToolInfo>
              </ToolItem>
            ))}
          </ToolsList>
        </ToolsSidebar>

        <ToolContent>
          {renderToolContent()}
        </ToolContent>
      </ToolsContainer>
    </motion.div>
  );
};

export default Tools;